import React, { useState, useEffect } from "react";
import {
  X, MapPin, Navigation, Camera, Loader2, Upload,
  CheckCircle, Search, CreditCard, FileText, UserCheck,
  FileCheck, Banknote, Shield, Phone,
} from "lucide-react";
import toast from "react-hot-toast";
import { ServiceFormHeader, ServiceFormFooter } from "./ServiceFormShell";
import api from "../../utils/axios";
import CustomTextEditor from "../UI/CustomTextEditor";
import VendorLiftAI from "./VendorLiftAI";
import ServiceAIAssistant from "./ServiceAIAssistant";
import AIPackageBuilder from "./AIPackageBuilder";

// ── Matches photographySchema.typeOfServices enum ──────────────────────────
const TYPE_OF_SERVICES = [
  "wedding_photography", "event_coverage", "drone_services",
  "live_streaming", "photo_booth", "cinematic_video",
  "virtual_reality", "other_photography",
];

// ── Matches pricing[].out_station enum ──────────────────────────────────────
const OUT_STATION_OPTIONS = [
  { value: "self", label: "Self (I travel)" },
  { value: "client", label: "Client Pays Travel" },
  { value: "no_travel", label: "No Outstation" },
];

const EMPTY_FORM = {
  name: "",
  serviceType: "photography",
  typeOfServices: [],
  description: "",
  paymentMethods: [],
  images: [],
  videos: [],
  logo: null,
  serviceLocation: { type: "Point", coordinates: [] },
  serviceAddress: { street: "", area: "", city: "", state: "", country: "India", pincode: "", landmark: "", fullAddress: "" },
  pricing: [], // array of packages per schema
  payoutBankDetails: { accountHolderName: "", accountNumber: "", ifscCode: "", bankName: "", branchName: "" },
  businessDocuments: { businessLicense: null, taxRegistration: null, gstCertificate: null, identityProof: null, bankProof: null, fssaiCertificate: null, liquorLicense: null },
  contact_details: { phone: "", alternative_phone: "", email: "" },
  isGst: false,
  isLive: true,
};

const PhotographyModal = ({ service, isOpen, onClose, onSave, inline = false }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  useEffect(() => {
    if (service) {
      setFormData({
        ...EMPTY_FORM,
        name: service.name || "",
        typeOfServices: service.typeOfServices || [],
        description: service.description || "",
        paymentMethods: service.paymentMethods || [],
        images: service.images || [],
        videos: service.videos || [],
        logo: service.logo || null,
        serviceLocation: service.serviceLocation || EMPTY_FORM.serviceLocation,
        serviceAddress: service.serviceAddress || EMPTY_FORM.serviceAddress,
        pricing: service.pricing || [],
        payoutBankDetails: service.payoutBankDetails || EMPTY_FORM.payoutBankDetails,
        businessDocuments: service.businessDocuments || EMPTY_FORM.businessDocuments,
        contact_details: service.contact_details || EMPTY_FORM.contact_details,
        isGst: service.isGst || false,
        isLive: service.isLive !== undefined ? service.isLive : true,
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setActiveTab("basic");
  }, [service, isOpen]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (locationSearch.length >= 2) searchLocations();
      else setLocationSuggestions([]);
    }, 300);
    return () => clearTimeout(t);
  }, [locationSearch]);

  if (!isOpen) return null;

  // ── Location ──────────────────────────────────────────────────────────────
  const searchLocations = async () => {
    try {
      setSearchingLocation(true);
      const res = await api.get(`/users/places/autocomplete?input=${encodeURIComponent(locationSearch)}`);
      if (res.data.success) setLocationSuggestions(res.data.data);
    } catch { } finally { setSearchingLocation(false); }
  };

  const selectLocation = async (s) => {
    try {
      const res = await api.get(`/users/address?lat=${s.latitude}&lng=${s.longitude}`);
      if (res.data.success) {
        setFormData(p => ({ ...p, serviceLocation: { type: "Point", coordinates: [s.longitude, s.latitude] }, serviceAddress: { ...p.serviceAddress, ...res.data.data, fullAddress: s.description } }));
      }
    } catch {
      setFormData(p => ({ ...p, serviceLocation: { type: "Point", coordinates: [s.longitude, s.latitude] }, serviceAddress: { ...p.serviceAddress, city: s.main_text, state: s.secondary_text?.split(",")[0], fullAddress: s.description, country: "India" } }));
    }
    setLocationSearch(""); setLocationSuggestions([]);
    toast.success("Location selected");
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("Not supported"); return; }
    toast.loading("Getting location...");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const res = await api.get(`/users/address?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
        if (res.data.success) {
          setFormData(p => ({ ...p, serviceLocation: { type: "Point", coordinates: [pos.coords.longitude, pos.coords.latitude] }, serviceAddress: { ...p.serviceAddress, ...res.data.data } }));
          toast.dismiss(); toast.success("Location set");
        }
      } catch { toast.dismiss(); toast.error("Failed"); }
    }, () => { toast.dismiss(); toast.error("Failed to get location"); });
  };

  // ── Uploads ───────────────────────────────────────────────────────────────
  const uploadFiles = async (files, setter) => {
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append("files", f));
    const res = await api.post("/image/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data.success ? res.data.data.files : [];
  };

  const handleImageUpload = async (files) => {
    try {
      setUploadingImages(true);
      const uploaded = await uploadFiles(files);
      setFormData(p => ({ ...p, images: [...p.images, ...uploaded.map(f => ({ url: f.url, caption: f.name }))] }));
      toast.success("Images uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploadingImages(false); }
  };

  const handleLogoUpload = async (files) => {
    try {
      setUploadingLogo(true);
      const uploaded = await uploadFiles(files);
      if (uploaded[0]) setFormData(p => ({ ...p, logo: uploaded[0].url }));
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploadingLogo(false); }
  };

  const handleDocUpload = async (files, docType) => {
    try {
      setUploadingDocs(true);
      const uploaded = await uploadFiles(files);
      if (uploaded[0]) setFormData(p => ({ ...p, businessDocuments: { ...p.businessDocuments, [docType]: { url: uploaded[0].url, name: uploaded[0].name } } }));
      toast.success("Document uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploadingDocs(false); }
  };

  // ── Pricing packages (array per schema) ───────────────────────────────────
  const addPackage = () => setFormData(p => ({ ...p, pricing: [...p.pricing, { packageName: "", price: 0, description: "", out_station: "no_travel" }] }));
  const updatePkg = (i, field, val) => setFormData(p => { const arr = [...p.pricing]; arr[i] = { ...arr[i], [field]: val }; return { ...p, pricing: arr }; });
  const removePkg = (i) => setFormData(p => ({ ...p, pricing: p.pricing.filter((_, j) => j !== i) }));

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.name) { toast.error("Name is required"); return; }
    if (!formData.serviceLocation?.coordinates?.length) { toast.error("Please select a location"); return; }
    if (!formData.serviceAddress?.city || !formData.serviceAddress?.state || !formData.serviceAddress?.pincode) { toast.error("Complete address required"); return; }
    try {
      setLoading(true);
      const res = service?._id
        ? await api.put(`/api/v1/services/${service._id}`, formData)
        : await api.post("/api/v1/services/", formData);
      if (res.data.success) { toast.success(service ? "Updated!" : "Created!"); onSave(); onClose(); }
    } catch (e) { toast.error(e.response?.data?.message || "Failed to save"); } finally { setLoading(false); }
  };

  const TABS = ["basic", "location", "media", "pricing", "packages", "documents", "payout"];

  return (
    <div className={inline ? "w-full" : "fixed inset-0 z-50 overflow-y-auto"}>
      {!inline && (
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        </div>
      )}
      <div className={inline ? "" : "relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-auto"}>

          <ServiceFormHeader
            icon={Camera}
            title={service ? "Edit Photography Service" : "Add Photography Service"}
            subtitle="Photography"
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onClose={onClose}
            inline={inline}
          />

          <div className="px-5 sm:px-7 py-6 space-y-6">

            {/* BASIC */}
            {activeTab === "basic" && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[13px] font-semibold text-gray-700">Service Name *</label>
                    <ServiceAIAssistant serviceType="photography" step="name" formData={formData} onApply={({ name }) => setFormData({ ...formData, name })} />
                  </div>
                  <input type="text" value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                    placeholder="e.g. Candid Wedding Photography" />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Description</label>
                  <div className="mb-3">
                    <VendorLiftAI
                      serviceType={formData.serviceType || "photography"}
                      formData={formData}
                      setFormData={setFormData}
                    />
                  </div>
                  <CustomTextEditor value={formData.description} onChange={html => setFormData({ ...formData, description: html })} />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Type of Services</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 rounded-xl border border-gray-200/70 bg-gray-50/60 p-3.5 max-h-48 overflow-y-auto">
                    {TYPE_OF_SERVICES.map(s => (
                      <label key={s} className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={formData.typeOfServices.includes(s)}
                          onChange={e => setFormData({ ...formData, typeOfServices: e.target.checked ? [...formData.typeOfServices, s] : formData.typeOfServices.filter(x => x !== s) })}
                          className="w-4 h-4  rounded focus:ring-purple-400 accent-purple-600 cursor-pointer" />
                        <span className="text-sm text-gray-700">{s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
                      </label>
                    ))}
                  </div>
                  {formData.typeOfServices.length > 0 && <p className="text-xs font-medium text-gray-500 mt-1.5">{formData.typeOfServices.length} selected</p>}
                </div>

               

                <div>
                  <label className="block text-[13px] font-semibold text-gray-700 mb-1.5"><CreditCard className="inline w-4 h-4 mr-1" />Payment Methods</label>
                  <div className="flex space-x-4">
                    {["cash", "card", "upi"].map(m => (
                      <label key={m} className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={formData.paymentMethods.includes(m)}
                          onChange={e => setFormData({ ...formData, paymentMethods: e.target.checked ? [...formData.paymentMethods, m] : formData.paymentMethods.filter(x => x !== m) })}
                          className="w-4 h-4  rounded accent-purple-600 cursor-pointer" />
                        <span className="text-sm capitalize">{m}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200/70 bg-gray-50/60 p-5 space-y-4">
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900 flex items-center space-x-2"><Phone className="w-4 h-4 text-purple-500" /><span>Contact Details</span></h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Phone *</label>
                      <input type="tel" value={formData.contact_details.phone}
                        onChange={e => setFormData({ ...formData, contact_details: { ...formData.contact_details, phone: e.target.value } })}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all" placeholder="+91 98765 43210" maxLength={15} />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Alternative Phone</label>
                      <input type="tel" value={formData.contact_details.alternative_phone}
                        onChange={e => setFormData({ ...formData, contact_details: { ...formData.contact_details, alternative_phone: e.target.value } })}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all" placeholder="+91 98765 43211" maxLength={15} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Email *</label>
                      <input type="email" value={formData.contact_details.email}
                        onChange={e => setFormData({ ...formData, contact_details: { ...formData.contact_details, email: e.target.value } })}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all" placeholder="photo@example.com" />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* LOCATION */}
            {activeTab === "location" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900">Service Location *</h3>
                  <button onClick={getCurrentLocation} className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg transition-all">
                    <Navigation className="w-4 h-4" /><span>Current Location</span>
                  </button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                  <input type="text" value={locationSearch} onChange={e => setLocationSearch(e.target.value)}
                    placeholder="Search location..." className="w-full pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all" />
                  {searchingLocation && <Loader2 className="absolute right-3 top-3.5 w-5 h-5 animate-spin text-purple-500" />}
                  {locationSuggestions.length > 0 && (
                    <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto mt-1">
                      {locationSuggestions.map((s, i) => (
                        <button key={i} onClick={() => selectLocation(s)} className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100">
                          <div className="flex items-start space-x-3">
                            <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div><div className="font-medium">{s.main_text}</div><div className="text-sm text-gray-500">{s.secondary_text}</div></div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {formData.serviceAddress?.fullAddress && (
                  <div className="p-4 bg-purple-50/60 border border-purple-200 rounded-xl flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div><p className="text-sm font-semibold text-gray-900">Location Selected</p><p className="text-sm text-gray-600">{formData.serviceAddress.fullAddress}</p></div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "street", label: "Street", ph: "Street address" },
                    { key: "area", label: "Area", ph: "Area/Locality" },
                    { key: "city", label: "City *", ph: "Hyderabad" },
                    { key: "state", label: "State *", ph: "Telangana" },
                    { key: "pincode", label: "PIN Code *", ph: "500001", max: 6 },
                    { key: "landmark", label: "Landmark", ph: "Near..." },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">{f.label}</label>
                      <input type="text" value={formData.serviceAddress?.[f.key] || ""}
                        onChange={e => setFormData({ ...formData, serviceAddress: { ...formData.serviceAddress, [f.key]: e.target.value } })}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                        placeholder={f.ph} maxLength={f.max} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MEDIA */}
            {activeTab === "media" && (
              <div className="space-y-6">
                <ServiceAIAssistant serviceType="photography" step="photos" formData={formData} />
                {/* Logo */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-['Manrope'] text-base font-bold text-gray-900">Logo</h3>
                    <label className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg transition-all cursor-pointer">
                      {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                      <span>Upload Logo</span>
                      <input type="file" accept="image/*" onChange={e => handleLogoUpload(e.target.files)} className="hidden" />
                    </label>
                  </div>
                  {formData.logo && (
                    <div className="relative inline-block group">
                      <img src={formData.logo} alt="Logo" className="w-24 h-24 object-cover rounded-2xl border border-gray-200/70" />
                      <button onClick={() => setFormData({ ...formData, logo: null })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
                {/* Images */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-['Manrope'] text-base font-bold text-gray-900">Gallery</h3>
                    <label className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg transition-all cursor-pointer">
                      {uploadingImages ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                      <span>Upload Images</span>
                      <input type="file" multiple accept="image/*" onChange={e => handleImageUpload(e.target.files)} className="hidden" />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((img, i) => (
                      <div key={i} className="relative group">
                        <img src={img.url} alt="" className="w-full h-28 object-cover rounded-xl border border-gray-200/70" />
                        <button onClick={() => setFormData({ ...formData, images: formData.images.filter((_, j) => j !== i) })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Videos */}
                <div>
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-3">Videos</h3>
                  <input type="url" placeholder="Paste video URL and press Enter"
                    className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white"
                    onKeyDown={e => { if (e.key === "Enter" && e.target.value) { setFormData({ ...formData, videos: [...formData.videos, { url: e.target.value, caption: "" }] }); e.target.value = ""; } }} />
                  <div className="space-y-2 mt-2">
                    {formData.videos.map((v, i) => (
                      <div key={i} className="flex items-center justify-between p-2 border rounded-lg">
                        <span className="text-sm truncate text-gray-600">{v.url}</span>
                        <button onClick={() => setFormData({ ...formData, videos: formData.videos.filter((_, j) => j !== i) })} className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* PRICING — array of packages per schema */}
            {activeTab === "pricing" && (
              <div className="space-y-4">
                <ServiceAIAssistant serviceType="photography" step="pricing" formData={formData} compact={false} />
                <div className="flex items-center justify-between">
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900">Pricing Packages</h3>
                  <button onClick={addPackage} className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg transition-all">+ Add Package</button>
                </div>
                {formData.pricing.length === 0 && (
                  <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                    <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No packages yet. Click "Add Package" to start.</p>
                  </div>
                )}
                {formData.pricing.map((pkg, idx) => (
                  <div key={idx} className="rounded-2xl border border-gray-200/70 bg-gray-50/60 p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800">Package {idx + 1}</h4>
                      <button onClick={() => removePkg(idx)} className="text-red-500 hover:text-red-700 text-sm">Remove</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Package Name *</label>
                        <input type="text" value={pkg.packageName} onChange={e => updatePkg(idx, "packageName", e.target.value)}
                          className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white"
                          placeholder="e.g. Basic / Premium / Wedding" />
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Price (₹) *</label>
                        <input type="number" min="0" value={pkg.price} onChange={e => updatePkg(idx, "price", parseInt(e.target.value) || 0)}
                          className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white" placeholder="e.g. 25000" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Outstation Policy</label>
                      <select value={pkg.out_station} onChange={e => updatePkg(idx, "out_station", e.target.value)}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white">
                        {OUT_STATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Package Description</label>
                      <textarea rows={3} value={pkg.description} onChange={e => updatePkg(idx, "description", e.target.value)}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all resize-none bg-white"
                        placeholder="What's included..." />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* PACKAGES */}
            {activeTab === "packages" && (
              <div className="space-y-4">
                {service?._id ? (
                  <AIPackageBuilder
                    serviceType="photography"
                    serviceData={service}
                    serviceId={service._id}
                  />
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center">
                    <p className="text-sm font-medium text-gray-600">Save the service first</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Fill the basic details and save, then reopen to build &amp; edit packages here.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* DOCUMENTS */}
            {activeTab === "documents" && (
              <div className="space-y-4">
                <h3 className="font-['Manrope'] text-base font-bold text-gray-900 flex items-center space-x-2"><FileText className="w-5 h-5 text-purple-500" /><span>Business Documents</span></h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { key: "businessLicense", label: "Business License", icon: FileCheck },
                    { key: "gstCertificate", label: "GST Certificate", icon: FileCheck },
                    { key: "identityProof", label: "Identity Proof (Aadhar/PAN)", icon: UserCheck },
                    { key: "bankProof", label: "Bank Proof", icon: Banknote },
                    { key: "fssaiCertificate", label: "FSSAI Certificate", icon: Shield },
                    { key: "liquorLicense", label: "Liquor License", icon: Shield },
                  ].map(({ key, label, icon: Icon }) => (
                    <div key={key} className="rounded-2xl border border-gray-200/70 bg-white p-4 hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/5 transition-all">
                      <label className="text-[13px] font-semibold text-gray-700 mb-1.5 flex items-center space-x-2">
                        <Icon className="w-4 h-4 text-purple-500" /><span>{label}</span>
                      </label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => handleDocUpload(e.target.files, key)} className="hidden" id={`phot-${key}`} />
                      <label htmlFor={`phot-${key}`} className="w-full flex items-center justify-center p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50/50 transition-all cursor-pointer">
                        {uploadingDocs ? <Loader2 className="w-5 h-5 animate-spin text-purple-500" /> : <><Upload className="w-4 h-4 text-gray-400 mr-2" /><span className="text-sm text-gray-600">Click to upload</span></>}
                      </label>
                      {formData.businessDocuments[key] && (
                        <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
                          <span className="text-xs text-purple-800 flex items-center space-x-1 truncate"><CheckCircle className="w-3 h-3 flex-shrink-0" /><span className="truncate">{formData.businessDocuments[key].name}</span></span>
                          <button onClick={() => setFormData({ ...formData, businessDocuments: { ...formData.businessDocuments, [key]: null } })} className="text-red-500 ml-1 flex-shrink-0"><X className="w-3 h-3" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PAYOUT */}
            {activeTab === "payout" && (
              <div>
                <h3 className="font-['Manrope'] text-base font-bold text-gray-900 flex items-center space-x-2 mb-4"><CreditCard className="w-5 h-5 text-purple-600" /><span>Payout Bank Details</span></h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "accountHolderName", label: "Account Holder Name", ph: "John Doe" },
                    { key: "accountNumber", label: "Account Number", ph: "123456789012" },
                    { key: "ifscCode", label: "IFSC Code", ph: "SBIN0001234" },
                    { key: "bankName", label: "Bank Name", ph: "State Bank of India" },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">{f.label}</label>
                      <input type="text" value={formData.payoutBankDetails[f.key]}
                        onChange={e => setFormData({ ...formData, payoutBankDetails: { ...formData.payoutBankDetails, [f.key]: f.key === "ifscCode" ? e.target.value.toUpperCase() : e.target.value } })}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all" placeholder={f.ph} />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Branch Name</label>
                    <input type="text" value={formData.payoutBankDetails.branchName}
                      onChange={e => setFormData({ ...formData, payoutBankDetails: { ...formData.payoutBankDetails, branchName: e.target.value } })}
                      className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all" placeholder="MG Road Branch" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <ServiceFormFooter
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            isLive={formData.isLive}
            onLiveChange={(isLive) => setFormData({ ...formData, isLive })}
            isGst={formData.isGst}
            onGstChange={(isGst) => setFormData({ ...formData, isGst })}
            onClose={onClose}
            onSave={handleSave}
            loading={loading}
            isEdit={!!service}
          />
      </div>
    </div>
  );
};

export default PhotographyModal;
